import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import { useProducts } from '../context/ProductsContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { track } from '../analytics/track';

const fmt = (n) => n.toFixed(2).replace('.', ',');
const BUNDLE_PRICE = 20.99;

export default function BundleBuilder() {
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const { products } = useProducts();
  const { guardPurchase } = useSiteSettings();
  const navigate = useNavigate();
  // Bundle libero: solo Lavapavimenti + Sgrassatori (Professional escluso)
  const eligible = products.filter(p => (p.category === 'lavapavimenti' || p.category === 'sgrassatori') && !p.hidden);

  useEffect(() => { track('bundle_view'); }, []);

  const toggle = (id) => {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  const selectedProducts = useMemo(() => selected.map(id => products.find(p => p.id === id)).filter(Boolean), [selected, products]);
  const listTotal = useMemo(() => selectedProducts.reduce((s, p) => s + (p?.price || 0), 0), [selectedProducts]);
  const saving = Math.max(0, listTotal - BUNDLE_PRICE);

  const checkoutBundle = () => {
    if (selected.length !== 3 || loading) return;
    if (guardPurchase()) return;
    track('bundle_complete', { value: BUNDLE_PRICE, currency: 'EUR' });
    navigate(`/checkout?bundle=${selected.join(',')}`);
  };

  return (
    <section data-testid="bundle-builder" className="section-padding bg-essenya-surface2">
      <div className="container-essenya">
        <div className="max-w-2xl mb-12">
          <div className="text-eyebrow mb-3">Bundle Builder</div>
          <h2 className="text-4xl md:text-5xl font-display font-medium mb-4">Crea il tuo rituale di pulizia.</h2>
          <p className="text-essenya-text text-lg">Scegli 3 prodotti tra lavapavimenti e sgrassatori profumati: il tuo kit casa è sempre <strong className="text-essenya-ink">{fmt(BUNDLE_PRICE)}€</strong>, spedizione gratuita inclusa.</p>
        </div>

        <div className="mb-8 flex items-center gap-4">
          <div className="flex-1 h-2 bg-white rounded-full overflow-hidden">
            <div className="h-full bg-essenya-ink transition-all duration-500" style={{ width: `${(selected.length / 3) * 100}%` }} />
          </div>
          <span className="text-sm font-medium whitespace-nowrap" data-testid="bundle-progress">{selected.length}/3 prodotti</span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-10">
          {eligible.map(p => {
            const isSel = selected.includes(p.id);
            const isFull = !isSel && selected.length >= 3;
            return (
              <button
                key={p.id}
                onClick={() => toggle(p.id)}
                disabled={isFull}
                data-testid={`bundle-toggle-${p.id}`}
                className={`relative text-left rounded-xl overflow-hidden border-2 transition-all ${isSel ? 'border-essenya-ink scale-[0.98]' : 'border-transparent hover:border-essenya-border'} ${isFull ? 'opacity-40 cursor-not-allowed' : ''}`}
              >
                <div className="aspect-square bg-white">
                  <img src={p.images[0]} alt={p.name} className="w-full h-full object-contain p-3" />
                </div>
                {isSel && (
                  <span className="absolute top-2 right-2 bg-essenya-ink text-white rounded-full w-7 h-7 flex items-center justify-center"><Check size={14} strokeWidth={2} /></span>
                )}
                <div className="bg-white p-3">
                  <p className="text-xs text-essenya-text">{p.categoryLabel}</p>
                  <p className="text-sm font-medium leading-tight">{p.name}</p>
                  <p className="text-sm font-display mt-1">{fmt(p.price)}€</p>
                </div>
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-xl p-6 md:p-8 flex flex-col md:flex-row items-center gap-6 justify-between">
          <div>
            <div className="text-eyebrow mb-2">Riepilogo bundle</div>
            <p className="font-display text-2xl flex items-baseline gap-3">
              <span className="text-essenya-ink" data-testid="bundle-final-price">{fmt(BUNDLE_PRICE)}€</span>
              {selected.length === 3 && saving > 0 && (
                <span className="text-base text-essenya-text line-through" data-testid="bundle-list-price">{fmt(listTotal)}€</span>
              )}
            </p>
            {selected.length === 3
              ? <p className="text-sm text-essenya-ink mt-1" data-testid="bundle-saving">{saving > 0 ? `Risparmi ${fmt(saving)}€ · Spedizione gratuita` : 'Spedizione gratuita inclusa'}</p>
              : <p className="text-sm text-essenya-text mt-1">Seleziona 3 prodotti per creare il tuo kit</p>}
          </div>
          <button
            data-testid="bundle-checkout"
            onClick={checkoutBundle}
            disabled={selected.length !== 3 || loading}
            className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? 'Attendere…' : `Vai al checkout · ${fmt(BUNDLE_PRICE)}€`}
          </button>
        </div>
      </div>
    </section>
  );
}
